import React from 'react';

import { ResponsiveContainer, ScatterChart, CartesianGrid, XAxis, YAxis, Tooltip, Scatter, Text } from 'recharts';
import { StyledPaper, StyledTooltip, StyledTypography } from 'styles/common';

import { getColorFromKey } from 'constants/helper';
import theme from 'constants/theme';

const TooltipContent = ({ payload, separator, metaData, metaDataMap, metaDataColorMap }) => (
  <StyledPaper padding="1rem">
    <StyledTypography variant="body1" weight={600}>
      {payload[0]?.value}
    </StyledTypography>
    {Object.entries(payload[0]?.payload[metaData] || {}).map((data) => (
      <StyledTypography variant="body2" color={metaDataColorMap[data[0]]}>
        {metaDataMap[data[0]] + separator + data[1]}
      </StyledTypography>
    ))}
  </StyledPaper>
);

const AxisLabel = ({ axisType, value, viewBox }) => {
  const { x, y, width, height } = viewBox;
  const isVertical = axisType === 'y';
  const defaultOffset = 10;

  const cx = isVertical ? x - defaultOffset - 5 : x + width / 2;
  const cy = isVertical ? height / 2 + y : y + height + defaultOffset;

  return (
    <Text x={cx} y={cy} angle={isVertical ? -90 : 0} textAnchor="middle">
      {value}
    </Text>
  );
};

const CustomXAxisTick = (props) => {
  const {
    textAnchor,
    x,
    y,
    payload: { value },
  } = props;

  return (
    <g>
      <foreignObject className="node" x={x - 25} y={y - 5} width={80} height={30}>
        <StyledTooltip title={value}>
          <text
            className="recharts-text recharts-cartesian-axis-tick-value"
            stroke="none"
            fill={theme.palette.text.secondary}
            textAnchor={textAnchor}
          >
            <tspan style={{ color: theme.palette.text.secondary }}>
              {value.length > 12 ? `${value.slice(0, 9)}...` : value}
            </tspan>
          </text>
        </StyledTooltip>
      </foreignObject>
    </g>
  );
};

const CustomYAxisTick = (props) => {
  const {
    textAnchor,
    x,
    y,
    payload: { value },
    formatter,
  } = props;

  const formattedValue = formatter(value).toString();
  const formattedValueLength = formattedValue.length;
  const toShowValue = formattedValueLength <= 9 ? formattedValue : `${formattedValue.slice(0, 6)}...`;
  const numOfSpaces = formattedValueLength >= 9 ? 0 : 9 - formattedValueLength;

  return (
    <g>
      <foreignObject className="node" x={x - 55} y={y - 10} width={70} height={30}>
        <StyledTooltip title={formattedValue}>
          <text
            className="recharts-text recharts-cartesian-axis-tick-value"
            stroke="none"
            fill={theme.palette.text.secondary}
            textAnchor={textAnchor}
          >
            <tspan style={{ color: theme.palette.text.secondary }}>
              {Array(numOfSpaces)
                .fill(0)
                .map(() => (
                  <span>&nbsp;</span>
                ))}
              {toShowValue}
            </tspan>
          </text>
        </StyledTooltip>
      </foreignObject>
    </g>
  );
};

const MeshScatterChart = ({
  name,
  data,
  xAxisAttributes,
  yAxisAttributes,
  fillColor,
  metaData,
  metaDataMap,
  metaDataColorMap,
}) => (
  <ResponsiveContainer>
    <ScatterChart
      margin={{
        top: 20,
        bottom: xAxisAttributes.label ? 15 : 0,
        left: yAxisAttributes.label ? 30 : 0,
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey={xAxisAttributes.dataKey}
        name={xAxisAttributes.name}
        stroke={theme.palette.background.lightGrey3}
        interval={0}
        tick={<CustomXAxisTick />}
        label={xAxisAttributes.label ? <AxisLabel axisType="x" value={xAxisAttributes.label} /> : null}
      />
      <YAxis
        dataKey={yAxisAttributes.dataKey}
        name={yAxisAttributes.name}
        type={yAxisAttributes.type}
        domain={yAxisAttributes.domain}
        ticks={yAxisAttributes.ticks}
        stroke={theme.palette.background.lightGrey3}
        tick={<CustomYAxisTick formatter={yAxisAttributes.tickFormatter} />}
        label={yAxisAttributes.label ? <AxisLabel axisType="y" value={yAxisAttributes.label} /> : null}
      />
      <Tooltip
        content={<TooltipContent metaData={metaData} metaDataMap={metaDataMap} metaDataColorMap={metaDataColorMap} />}
      />
      <Scatter data={data} name={name} fill={getColorFromKey(fillColor)} />
    </ScatterChart>
  </ResponsiveContainer>
);

export default MeshScatterChart;
